import { Button } from "@talladega/components";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import LocalImage from "@common/components/LocalImage";
import Image404 from "@public/images/404-wrong-turn.png";
import { ReactElement, useEffect } from "react";
import PublicLayout from "@layouts/public/components/PublicLayout";
import { GetStaticPropsContext } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { gtmPushData } from "@talladega/helpers";
import { hotjar, Event as HotjarEvent } from "@modules/core/hotjar/types";
import {
  ArrowRightIcon,
  BookOpenIcon,
  DollarCoinIcon,
  MessageChatIcon,
} from "@talladega/icons";
import { useRouter } from "next/router";
import useUtmParamsFromQuery from "@modules/query-string/hooks/useUtmParamsFromQuery";

const NotFound = () => {
  const { t } = useTranslation(["common"]);

  const router = useRouter();
  const locale = router.locale || "en";
  const utmParams = useUtmParamsFromQuery();

  useEffect(() => {
    gtmPushData({
      event: "404 Page Not Found",
      path: window.location.href,
    });
    hotjar.event(HotjarEvent.PageNotFound);
  }, []);

  const handleOpenIcoModal = () => {
    window.acIco.destroy();
    window.acIco.initModal("ico", "throttle", {
      data: undefined,
      locale,
      utmParams: {
        utmSource: utmParams.utmSource ?? "throttle",
        utmMedium: utmParams.utmMedium ?? "web",
        utmCampaign: utmParams.utmCampaign ?? "404-page",
        utmContent: utmParams.utmContent ?? "get-offer-cta",
      },
    });
  };

  return (
    <>
      <div className="h-full bg-white py-12 flex items-center justify-center">
        <div className="max-w-page bg-white flex flex-col items-center px-4 text-center md:px-20 xl:px-0">
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center">
              <LocalImage
                src={Image404}
                alt="Page not found image"
                priority
                width={400}
                height={400}
              />
            </div>
          </div>

          <h1 className="pb-4 md:pt-4 text-4xl lg:text-6xl text-primary">
            {t("common:oops_wrong_turn")}
          </h1>

          <div className="m-6 flex items-center justify-center">
            <p className="lg:w-[768px] text-xl text-gray-600">
              {t("common:looks_like_youve_taken_a_detour")}
            </p>
          </div>

          <Link href="/">
            <Button className="capitalize" buttonStyle="primary">
              {t("common:go_home")}
            </Button>
          </Link>
        </div>
      </div>

      <div className="py-4 my-2 max-w-3xl mx-6 md:mx-auto">
        <div className="border-t w-full max-w-lg mx-auto" />

        <div
          className="flex items-center justify-between gap-4 py-6 transition w-full max-w-lg mx-auto cursor-pointer"
          tabIndex={0}
          onClick={() => handleOpenIcoModal()}
          onKeyDown={() => handleOpenIcoModal()}
          role="button"
        >
          <div className="p-[12px] bg-white shadow-xs-skeuomorphic">
            <DollarCoinIcon className="h-6 w-6 text-primary-600" />
          </div>

          <div className="flex-1 text-left">
            <p className="text-lg font-semibold text-gray-900">
              {t("common:get_an_instant_offer")}
            </p>
            <p className="text-sm text-gray-600">
              {t("common:sell_your_car_in_few_clicks")}
            </p>
          </div>

          <ArrowRightIcon className="h-5 w-5 text-gray-500" />
        </div>

        <div className="border-t w-full max-w-lg mx-auto" />

        <Link
          href="/#how-it-works"
          className="flex items-center justify-between gap-4 py-6 transition w-full max-w-lg mx-auto"
        >
          <div className="p-[12px] bg-white shadow-xs-skeuomorphic">
            <BookOpenIcon className="h-6 w-6 text-primary-600" />
          </div>

          <div className="flex-1 text-left">
            <p className="text-lg font-semibold text-gray-900">
              {t("common:how_it_works")}
            </p>
            <p className="text-sm text-gray-600">
              {t("common:learn_how_easy_it_is")}
            </p>
          </div>

          <ArrowRightIcon className="h-5 w-5 text-gray-500" />
        </Link>

        <div className="border-t w-full max-w-lg mx-auto" />

        <Link
          href="/contact"
          className="flex items-center justify-between gap-4 py-6 transition w-full max-w-lg mx-auto"
        >
          <div className="p-[12px] bg-white shadow-xs-skeuomorphic">
            <MessageChatIcon className="h-6 w-6 text-primary-600" />
          </div>

          <div className="flex-1 text-left">
            <p className="text-lg font-semibold text-gray-900">
              {t("common:contact_us")}
            </p>
            <p className="text-sm text-gray-600">
              {t("common:still_lost_happy_to_help")}
            </p>
          </div>

          <ArrowRightIcon className="h-5 w-5 text-gray-500" />
        </Link>

        <div className="border-t w-full max-w-lg mx-auto" />
      </div>
    </>
  );
};

NotFound.pageName = "NotFound";
NotFound.getLayout = function getLayout(page: ReactElement) {
  return <PublicLayout>{page}</PublicLayout>;
};

export async function getStaticProps(context: GetStaticPropsContext) {
  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? "en", [
        "common",
        "common-components",
      ])),
    },
  };
}

export default NotFound;
